import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Download, MessageCircle, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fcfa, fdate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Loader, StatusBadge } from "@/components/common";
import { CodeInput } from "@/pages/public/Login";

const useLoad = (url) => {
  const [data, setData] = useState(null);
  const load = () => api.get(url).then(({ data: d }) => setData(d)).catch(() => setData(false));
  useEffect(() => {
    load();
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps
  return [data, load];
};

export function InvitationsTab() {
  const [data, load] = useLoad("/me/invitations");
  const [f, setF] = useState({ invitee_name: "", invitee_phone: "", invitee_email: "" });
  const create = async (e) => {
    e.preventDefault();
    try {
      const { data: inv } = await api.post("/me/invitations", f);
      setF({ invitee_name: "", invitee_phone: "", invitee_email: "" });
      load();
      window.open(inv.whatsapp_url, "_blank", "noopener");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const revoke = async (id) => {
    await api.delete(`/me/invitations/${id}`);
    toast.success("Invitation révoquée");
    load();
  };
  if (!data) return <Loader />;
  const left = data.quota - data.used_this_year;
  return (
    <div className="space-y-4">
      <form onSubmit={create} className="mr-card space-y-3" data-testid="invite-form">
        <p className="font-medium">Inviter un entrepreneur</p>
        <p className="text-sm text-mr-muted tabular" data-testid="invite-quota">{left} invitation{left > 1 ? "s" : ""} restante{left > 1 ? "s" : ""} cette année · code valable {data.validity_days} jours, usage unique</p>
        <input data-testid="invite-name-input" className="mr-input" placeholder="Nom de la personne" value={f.invitee_name} onChange={(e) => setF({ ...f, invitee_name: e.target.value })} />
        <input data-testid="invite-phone-input" className="mr-input" placeholder="WhatsApp (facultatif)" inputMode="tel" value={f.invitee_phone} onChange={(e) => setF({ ...f, invitee_phone: e.target.value })} />
        <button data-testid="invite-create-button" className="mr-btn-primary w-full" disabled={left <= 0 || f.invitee_name.length < 2}><MessageCircle size={16} /> Créer et envoyer par WhatsApp</button>
      </form>
      {data.items.map((i) => (
        <div key={i.id} className="mr-card p-4 space-y-2" data-testid="invitation-row">
          <div className="flex items-center justify-between gap-3"><p className="font-medium text-sm">{i.invitee_name || "Invitation"} · <span className="tabular tracking-widest">{i.code}</span></p><StatusBadge status={i.status} /></div>
          <p className="text-xs text-mr-muted tabular">Expire le {fdate(i.expires_at)}</p>
          {i.status === "envoyee" && (
            <div className="flex flex-wrap gap-2">
              <a href={i.whatsapp_url} target="_blank" rel="noreferrer" className="mr-btn-secondary px-3" data-testid="invitation-whatsapp-link"><MessageCircle size={15} /> WhatsApp</a>
              <button className="mr-btn-ghost px-3" data-testid="invitation-copy-button" onClick={() => { navigator.clipboard?.writeText(i.link); toast.success("Lien copié"); }}><Copy size={15} /> Copier le lien</button>
              <button className="mr-btn-danger px-3" data-testid="invitation-revoke-button" onClick={() => revoke(i.id)}>Révoquer</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function OpinionCard({ r, onDone }) {
  const [note, setNote] = useState("");
  const send = async (knows) => {
    try {
      await api.post(`/me/sponsor-requests/${r.id}`, { knows, note });
      toast.success("Avis transmis au bureau");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mr-card space-y-3" data-testid="sponsor-request-row">
      <p className="font-medium">{r.candidate_name}</p>
      <p className="text-sm text-mr-muted">{r.company} · {r.city}</p>
      <textarea data-testid="sponsor-note-input" className="mr-input min-h-[80px]" placeholder="Votre note sur ce candidat" value={note} onChange={(e) => setNote(e.target.value)} />
      <div className="grid grid-cols-2 gap-2">
        <button data-testid="sponsor-confirm-button" className="mr-btn-primary" onClick={() => send(true)}>Je le connais</button>
        <button data-testid="sponsor-deny-button" className="mr-btn-secondary" onClick={() => send(false)}>Je ne le connais pas</button>
      </div>
    </div>
  );
}

export function SponsorTab() {
  const [items, load] = useLoad("/me/sponsor-requests");
  if (!items) return <Loader />;
  if (!items.length) return <p className="text-sm text-mr-text2" data-testid="sponsor-empty">Aucun avis de parrainage en attente.</p>;
  return <div className="space-y-3">{items.map((r) => <OpinionCard key={r.id} r={r} onDone={load} />)}</div>;
}

export function ActivityTab() {
  const [o] = useLoad("/me/overview");
  if (!o) return <Loader />;
  return (
    <div className="space-y-6">
      <section><p className="font-medium mb-3">Mes prochaines échéances</p>
        {o.dues.length === 0 ? <p className="text-sm text-mr-text2">Aucune échéance.</p> : <ul className="space-y-2">{o.dues.map((d, k) => (
          <li key={k} className="mr-card p-4 flex justify-between gap-3 text-sm" data-testid="due-row"><span>{d.label}</span><span className="tabular text-mr-muted">{d.amount ? `${fcfa(d.amount)} · ` : ""}{d.due_at ? fdate(d.due_at) : "après validation"}</span></li>))}</ul>}
      </section>
      <section><p className="font-medium mb-3">Mes opérations</p>
        {o.operations.length === 0 ? <p className="text-sm text-mr-text2">Vous ne participez à aucune opération.</p> : <ul className="space-y-2">{o.operations.map((op) => (
          <li key={op.id}><Link to={`/app/operations/${op.id}`} className="mr-card p-4 flex justify-between gap-3 text-sm" data-testid="my-operation-row"><span>{op.title}</span><StatusBadge status={op.role} label={op.role === "adherent" ? "Adhérent" : "Intéressé"} /></Link></li>))}</ul>}
      </section>
      <section><p className="font-medium mb-3">Mes contacts</p>
        {o.contacts.length === 0 ? <p className="text-sm text-mr-text2">Aucun contact pour l'instant.</p> : <ul className="space-y-2">{o.contacts.map((c) => (
          <li key={c.id}><Link to={`/app/reseau/${c.id}`} className="mr-card p-4 flex justify-between gap-3 text-sm" data-testid="my-contact-row"><span>{c.name} <span className="text-mr-muted">· {c.company}</span></span>{c.status !== "accepte" && <StatusBadge status={c.status} label={c.incoming ? "À accepter" : "En attente"} />}</Link></li>))}</ul>}
      </section>
      <section><p className="font-medium mb-3">Mes fournisseurs sauvegardés</p>
        {o.saved_suppliers.length === 0 ? <p className="text-sm text-mr-text2">Aucun fournisseur sauvegardé. Ajoutez-en depuis les fiches fournisseurs.</p> : <ul className="space-y-2">{o.saved_suppliers.map((s) => (
          <li key={s.id} className="mr-card p-4 flex justify-between gap-3 text-sm" data-testid="saved-supplier-row"><span><Star size={14} className="inline text-mr-accent mr-1" />{s.name}</span><StatusBadge status={s.verification} label={s.verification === "verifie" ? "Vérifié" : "Non vérifié"} /></li>))}</ul>}
      </section>
    </div>
  );
}

export function SecurityTab() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [cf, setCf] = useState("");
  const save = async () => {
    if (nw !== cf) return toast.error("Les deux codes ne correspondent pas.");
    try {
      await api.post("/auth/change-code", { current: cur, new: nw });
      setCur("");
      setNw("");
      toast.success("Code personnel modifié");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mr-card space-y-4" data-testid="security-card">
      <p className="font-medium">Changer mon code personnel</p>
      <label className="block"><span className="mr-label">Code actuel</span><CodeInput testId="security-current-input" value={cur} onChange={setCur} /></label>
      <label className="block"><span className="mr-label">Nouveau code</span><CodeInput testId="security-new-input" value={nw} onChange={setNw} /></label>
      <label className="block"><span className="mr-label">Confirmer le nouveau code</span><CodeInput testId="security-confirm-input" value={cf} onChange={setCf} /></label>
      <button data-testid="security-save-button" className="mr-btn-primary w-full" disabled={cur.length !== 6 || nw.length !== 6} onClick={save}>Enregistrer</button>
    </div>
  );
}

export function DataTab() {
  const { logout } = useAuth();
  const [confirm, setConfirm] = useState("");
  const exportData = async () => {
    const { data } = await api.get("/me/export");
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "mes-donnees-monrezo.json";
    a.click();
  };
  const del = async () => {
    try {
      await api.post("/me/delete-account", { confirm });
      toast.success("Votre compte a été supprimé.");
      await logout();
      window.location.href = "/";
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mr-card space-y-4">
      <p className="text-sm text-mr-text2">Conformément à la loi ivoirienne sur les données personnelles, vous pouvez obtenir une copie de vos données ou supprimer votre compte.</p>
      <button data-testid="data-export-button" className="mr-btn-secondary w-full" onClick={exportData}><Download size={16} /> Exporter mes données</button>
      <div className="border-t border-mr-border pt-4 space-y-3">
        <p className="text-sm">Pour supprimer définitivement votre compte, saisissez <strong>SUPPRIMER</strong>.</p>
        <input data-testid="delete-confirm-input" className="mr-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        <button data-testid="data-deletion-button" className="mr-btn-danger w-full" disabled={confirm !== "SUPPRIMER"} onClick={del}><Trash2 size={16} /> Supprimer mon compte</button>
      </div>
    </div>
  );
}
